import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_SERVER } from '../config/env';
import { getAuthToken } from '../services/authSession';

const SOCKET_URL = typeof API_SERVER === 'string' ? API_SERVER.trim().replace(/\/+$/, '') : '';

/**
 * Avisa a tela quando o webhook do ZapSign atualiza o contrato do anunciante.
 * @param {number|string|null|undefined} advertiserId
 * @param {{ onUpdate?: (payload: object) => void, enabled?: boolean }} [options]
 */
export function useAdvertiserContractSocket(advertiserId, options = {}) {
    const { onUpdate, enabled = true } = options;
    const [connected, setConnected] = useState(false);
    const onUpdateRef = useRef(onUpdate);
    const id = Number(advertiserId);
    const room = Number.isFinite(id) && id > 0 ? `advertiser:${id}:contract` : '';

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    useEffect(() => {
        if (!enabled || !SOCKET_URL || !room) {
            setConnected(false);
            return undefined;
        }

        const token = getAuthToken();
        if (!token) {
            setConnected(false);
            return undefined;
        }

        const socket = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 12,
            reconnectionDelay: 1500,
        });

        const onConnect = () => {
            setConnected(true);
            socket.emit('join:room', room);
        };

        const onDisconnect = () => setConnected(false);

        const onContractUpdate = payload => {
            if (payload?.advertiserId && Number(payload.advertiserId) !== id) return;
            onUpdateRef.current?.(payload);
        };

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('advertiser:contract:update', onContractUpdate);

        if (socket.connected) onConnect();

        return () => {
            socket.emit('leave:room', room);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('advertiser:contract:update', onContractUpdate);
            socket.disconnect();
            setConnected(false);
        };
    }, [enabled, id, room]);

    return { connected, room };
}

export default useAdvertiserContractSocket;
